import { notifySuccess } from '@/components'
import {
  useFetchFavoritesListQuery,
  useFetchFavoritesWithDevicesQuery,
  useUpdateFavoriteDeviceMutation,
} from '@/components/Organisms/Favorites/service'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuList,
} from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

type FavoritesProps = {
  favoriteIds?: number[]
  onFavoriteListChange?: (ids: number[]) => void
}

const favoriteIdsEqual = (a: number[], b: number[]) => {
  if (a.length !== b.length) {
    return false
  }
  const sa = [...a].sort((x, y) => x - y)
  const sb = [...b].sort((x, y) => x - y)
  return sa.every((v, i) => v === sb[i])
}

const Favorites: React.FC<FavoritesProps> = ({
  favoriteIds: favoriteIdsProp,
  onFavoriteListChange,
}) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const deviceId = Number(id) || 0

  const { data: favoritesList, isLoading: isLoadingList } =
    useFetchFavoritesListQuery()
  const { data: favoritesDetail } = useFetchFavoritesWithDevicesQuery()

  const rows = React.useMemo(() => {
    if (!favoritesList?.data) {
      return []
    }
    return favoritesList.data.map((item) => {
      const detail = favoritesDetail?.data.find((d) => d.id === item.id)
      return {
        id: item.id,
        name: item.name,
        devices: detail?.devices.map((d) => d.id) ?? [],
      }
    })
  }, [favoritesList, favoritesDetail])

  const [updateFavoriteDevice, { isLoading: isUpdating }] =
    useUpdateFavoriteDeviceMutation()

  const [draftFavoriteIds, setDraftFavoriteIds] = React.useState<number[]>([])
  const draftFavoriteIdsRef = React.useRef<number[]>([])
  draftFavoriteIdsRef.current = draftFavoriteIds

  const initialFavoriteIdsRef = React.useRef<number[]>([])

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const idsForStar = open
    ? draftFavoriteIds
    : favoriteIdsProp !== undefined
      ? favoriteIdsProp
      : draftFavoriteIds

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const initial =
      favoriteIdsProp !== undefined
        ? [...favoriteIdsProp]
        : rows
            .filter((item) => item.devices.includes(deviceId))
            .map((item) => item.id)
            .filter((fid): fid is number => fid != null)
    initialFavoriteIdsRef.current = initial
    setDraftFavoriteIds(initial)
    setAnchorEl(event.currentTarget)
  }

  const toggleFavoriteId = (fid: number) => {
    setDraftFavoriteIds((prev) =>
      prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid]
    )
  }

  const commitSelection = React.useCallback(
    async (ids: number[]) => {
      if (deviceId !== 0) {
        try {
          const res = await updateFavoriteDevice({
            deviceId,
            favoriteIds: ids,
          }).unwrap()
          if (res.success) {
            notifySuccess('Избранное обновлено')
          }
        } catch (e) {
          console.error(e)
        }
      }

      onFavoriteListChange?.(ids)
    },
    [deviceId, onFavoriteListChange, updateFavoriteDevice]
  )

  const handleMenuClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown' | 'tabKeyDown' | 'menuItemClick'
  ) => {
    if (reason === 'menuItemClick') {
      return
    }

    setAnchorEl(null)

    if (favoritesList?.data?.length && favoritesList.data.length > 0) {
      const next = draftFavoriteIdsRef.current
      if (!favoriteIdsEqual(next, initialFavoriteIdsRef.current)) {
        void commitSelection(next)
      }
    }
  }

  const handleEmptyMenuItemClick = () => {
    setAnchorEl(null)
    navigate('/favorites')
  }

  const isInAnyFavorite =
    idsForStar.length > 0 ||
    rows.some((item) => item.devices.includes(deviceId))

  return (
    <Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        sx={{ maxHeight: 450 }}
      >
        {favoritesList?.data?.length && favoritesList.data.length > 0 ? (
          <div>
            <span style={{ paddingLeft: 5, color: '#0072BC' }}>Избранное</span>
            <Divider />
            <MenuList>
              {rows.map((item, index) => (
                <MenuItem
                  key={item.id ?? index}
                  id={item.id?.toString()}
                  onClick={() => {
                    if (item.id != null) {
                      toggleFavoriteId(item.id)
                    }
                  }}
                >
                  <ListItemIcon>
                    {draftFavoriteIds.includes(item.id ?? -1) ? (
                      <StarIcon
                        fontSize='small'
                        htmlColor='#e4dc42'
                      />
                    ) : (
                      <StarBorderIcon fontSize='small' />
                    )}
                  </ListItemIcon>
                  {item.name}
                </MenuItem>
              ))}
            </MenuList>
          </div>
        ) : (
          <div>
            <span style={{ paddingLeft: 5, color: '#0072BC' }}>
              Нет разделов
            </span>
            <Divider />
            <MenuItem
              id='1'
              onClick={handleEmptyMenuItemClick}
            >
              Создать раздел?
            </MenuItem>
          </div>
        )}
      </Menu>
      {isLoadingList || isUpdating ? (
        <CircularProgress size={35} />
      ) : (
        <IconButton onClick={handleClick}>
          <Tooltip
            title='Добавить в избранное'
            placement='top'
          >
            <StarIcon sx={{ fill: isInAnyFavorite ? '#FFD632' : '#999999' }} />
          </Tooltip>
        </IconButton>
      )}
    </Box>
  )
}

export default Favorites
